import { useCallback, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { ControllerFontType, makeStyles } from '@bymason-private/controller-v1-theme-provider';
import {
  Button,
  Controller,
  ControllerValidatedTextField,
  ControllerValidatedPasswordField,
  LinearProgress,
  Text,
  useForm,
  yupResolver,
} from '@bymason-private/legacy-pattern-components';
import { useToast, SIGNUP_LEGAL_TEXT } from '@bymason-private/controller-shared-widgets';
import { SignupFormFields, useSubmitSignupForm } from '@bymason-private/controller-api-hooks';
import { ControllerVersion } from '../../global-state/hooks';
import { RouterButton } from '@bymason-private/route-provider';

import { routes } from '../../router';
import { Fields, FormData, schema } from './types';
import { ControllerLogo } from './controller-logo';
import SignUpSuccess from './signup-success';

const useStyles = makeStyles((theme) => ({
  linearProgressRoot: {
    left: 0,
    position: 'absolute',
    top: 0,
    width: '100%',
  },
  formContainer: {
    borderRadius: 5,
    backgroundColor: theme.palette.background.default,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    width: 320,
    marginTop: 72,
    marginBottom: 40,
    flexBasis: 500,
    transition: 'all 0.3s ease-in-out',
    padding: '48px 20px',
    '& > *': {
      width: '100%',
    },
    '& > *:not(:last-child)': {
      marginBottom: 16,
    },
    [theme.breakpoints.down('sm')]: {
      margin: '10%',
    },
  },
  formContainerSuccess: {
    flexBasis: 200,
  },
  formSection: {
    background:
      'url("https://bymason.com/wp-content/uploads/2020/07/device_mgmt_hero-1.png") center center / cover no-repeat fixed',
    backgroundColor: theme.palette.background.default,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'center',
    minHeight: '100vh',
    width: '100%',
  },
  fullNameRow: {
    backgroundColor: 'inherit',
    display: 'flex',
    width: '100%',
    '& > *:first-child': {
      marginRight: 8,
    },
  },
  label: {
    display: 'inline-flex',
    letterSpacing: '0.14994px',
    // flex-basis fix for safari issue with checkbox too small as a flex child
    // https://stackoverflow.com/questions/34220553/flexbox-resizes-a-checkbox-too-small/34220944
    '& > span': {
      flexBasis: '100%',
    },
    '& > input': {
      flexBasis: '16px',
    },
  },
  button: {
    color: 'white',
    marginBottom: 16,
    '&:hover': {
      color: 'white',
      backgroundColor: 'transparent',
    },
  },
}));

export default function SignUpView() {
  const [setToastMessage, setToastSeverity] = useToast();
  const classes = useStyles();
  const searchParams = useMemo(() => new URLSearchParams(location.search), []);
  const fieldDefaults = useMemo(
    () => ({
      email: searchParams.get('email') || '',
      firstname: searchParams.get('firstName') || '',
      lastname: searchParams.get('lastName') || '',
      companyName: searchParams.get('companyName') || '',
      companyId: searchParams.get('companyId') || undefined,
      token: searchParams.get('token') || undefined,
      gclid: searchParams.get('gclid') || undefined,
      utmCampaign: searchParams.get('utm_campaign') || undefined,
      utmMedium: searchParams.get('utm_medium') || undefined,
      utmSource: searchParams.get('utm_source') || undefined,
      utmTerm: searchParams.get('utm_term') || undefined,
    }),
    [searchParams]
  );
  const invitedSignUp = useMemo(
    () => !!fieldDefaults.companyName && !!fieldDefaults.companyId && !!fieldDefaults.token,
    [fieldDefaults]
  );

  const [createAccount, createAccountResp, createAccountError, createAccountBusy] = useSubmitSignupForm();
  const [showSuccess, setShowSuccess] = useState(false);

  const {
    control,
    formState: { isValid },
    getValues,
    reset,
    trigger,
  } = useForm<FormData>({
    mode: 'all',
    defaultValues: {
      [Fields.FirstName]: fieldDefaults.firstname,
      [Fields.LastName]: fieldDefaults.lastname,
      [Fields.Company]: fieldDefaults.companyName,
      [Fields.Email]: fieldDefaults.email,
      [Fields.Password]: '',
      [Fields.CommunicationConsented]: false,
    },
    resolver: yupResolver(schema),
  });

  const handleSubmit = useCallback(() => {
    trigger();

    if (isValid) {
      const { consented, firstname, lastname, companyName, email, password } = getValues();
      const { companyId, token, gclid, utmCampaign, utmMedium, utmSource, utmTerm } = fieldDefaults;

      const data: SignupFormFields = {
        firstname: firstname.trim(),
        lastname: lastname.trim(),
        companyName: companyName.trim(),
        password,
        email: email.trim(),
        legal: {
          text: SIGNUP_LEGAL_TEXT,
          consented,
        },
        companyId,
        token,
        optInControllerVersion: ControllerVersion.ControllerV2,
        gclid,
        utmCampaign,
        utmMedium,
        utmSource,
        utmTerm,
      };

      createAccount(data);
    }
  }, [createAccount, fieldDefaults, getValues, isValid, trigger]);

  useEffect(() => {
    if (typeof createAccountResp === 'undefined') {
      setShowSuccess(true);
      reset();
    }
  }, [createAccountResp, reset]);

  useEffect(() => {
    if (createAccountError) {
      setToastMessage(createAccountError);
      setToastSeverity('error');
    }
  }, [createAccountError, setToastMessage, setToastSeverity]);

  return (
    <section className={classes.formSection}>
      {createAccountBusy && <LinearProgress classes={{ root: classes.linearProgressRoot }} />}
      <form className={clsx({ [classes.formContainer]: true, [classes.formContainerSuccess]: showSuccess })}>
        {showSuccess ? (
          <SignUpSuccess invitedSignUp={invitedSignUp} />
        ) : (
          <>
            <ControllerLogo />
            {invitedSignUp && (
              <Text variant={ControllerFontType.Body400R} component='div' textAlign='center'>
                <span>You&apos;ve been invited to join the organization </span>
                <strong>{fieldDefaults.companyName}</strong>
                <span>. Please finish setting up your account.</span>
              </Text>
            )}
            <div className={classes.fullNameRow}>
              <ControllerValidatedTextField
                label='First Name'
                name={Fields.FirstName}
                id={'given-name'}
                autoComplete='given-name'
                autoFocus={!fieldDefaults.firstname}
                required
                fullWidth
                control={control}
                readOnlyDisabled={!!fieldDefaults.firstname && invitedSignUp}
              />
              <ControllerValidatedTextField
                label='Last Name'
                name={Fields.LastName}
                id={'family-name'}
                autoComplete='family-name'
                required
                fullWidth
                control={control}
                readOnlyDisabled={!!fieldDefaults.lastname && invitedSignUp}
              />
            </div>
            <ControllerValidatedTextField
              label='Company Name'
              name={Fields.Company}
              id='organization'
              autoComplete='organization'
              required
              fullWidth
              control={control}
              readOnlyDisabled={invitedSignUp}
            />
            <ControllerValidatedTextField
              label='Email'
              name={Fields.Email}
              id='email'
              autoComplete='email'
              type='email'
              required
              fullWidth
              control={control}
              readOnlyDisabled={invitedSignUp}
            />
            <ControllerValidatedPasswordField
              label='Password'
              name={Fields.Password}
              id='new-password'
              autoComplete='new-password'
              type='password'
              autoFocus={invitedSignUp}
              required
              fullWidth
              control={control}
            />
            <Controller
              control={control}
              name={Fields.CommunicationConsented}
              render={({ field }) => {
                const { value, ...otherField } = field;
                const replaceWithLink = 'Privacy Policy';

                return (
                  <label className={classes.label} htmlFor='sign-up-consent'>
                    <input id='sign-up-consent' type='checkbox' checked={value} {...otherField} />
                    <Text variant={ControllerFontType.Body300R}>
                      {SIGNUP_LEGAL_TEXT.replace(replaceWithLink, '')}
                      <a target='_blank' rel='noopener noreferrer' href='http://bymason.com/Privacy-Policy'>
                        {replaceWithLink}
                      </a>
                    </Text>
                  </label>
                );
              }}
            />

            <Button
              disabled={!isValid && !createAccountBusy}
              onClick={handleSubmit}
              size='large'
              fullWidth
              variant='contained'
            >
              Sign up
            </Button>

            <Text variant={ControllerFontType.Body300R}>
              <span>By signing up with Mason, you are agreeing to our </span>
              <a target='_blank' rel='noopener noreferrer' href='http://bymason.com/Terms-of-Service'>
                Terms of Service
              </a>
            </Text>
          </>
        )}
      </form>
      {!showSuccess && (
        <RouterButton
          buttonComponent={
            <Button className={classes.button} disableTouchRipple>
              Go to login
            </Button>
          }
          route={routes.Login}
        />
      )}
    </section>
  );
}
